import Head from 'next/head';
import { NextSeo } from 'next-seo';
import { Content } from '@/screens/Content';

import styled from 'styled-components';

//logos
import Logo from '@/public/images/logos/LoadsmartLogo.svg';

const responsive = `
    width: 100%;
    height: 100%;
`;

const LoadsmartLogo = styled(Logo)`
  ${responsive};
`;

// data
const heroImages = {
  xxlarge: 'images/content/hero/hero-loadsmart-01-xxlg@2x.jpg',
  large: 'images/content/hero/hero-loadsmart-01-lg@2x.jpg',
  medium: 'images/content/hero/hero-loadsmart-01-md@2x.jpg',
  small: 'images/content/hero/hero-loadsmart-01-sm@2x.jpg',
};

const contentCopy = {
  date: `February 23, 2023`,
  title: `What is Port Drayage?`,
  slug: `what-is-port-drayage`,
  highlight: (
    <>
      When goods are transported short distances between an ocean port and other
      intermodal or final destination points, this is referred to as port
      drayage—or sometimes just drayage. Centuries ago, these goods were hauled
      by a dray, a horse-drawn wagon, or cart.
    </>
  ),
  copy: (
    <>
      <p>
        Additionally, and just as a point of reference, drayage is a term that
        is mostly used in the container shipping industry. Drayage and
        intermodal transport differ in that drayage happens via roadways, while
        intermodal transport involves multiple modes of transportation. Drayage
        is essentially part of a larger transport chain that turns into an
        intermodal shipment—or vice versa.{' '}
      </p>
      <p>
        That’s the short and sweet answer, but there’s a whole lot more involved
        in drayage in general. The following is something of a Drayage 101 class
        to bring you up to speed on this vital aspect of supply chain logistics.
      </p>

      <nav>
        <ul>
          <li>
            <a href="#What-Are-the-Benefits-of-Port-Drayage">
              What Are the Benefits of Port Drayage?
            </a>
          </li>
          <li>
            <a href="#What-is-import-drayage">What is import drayage?</a>
          </li>
          <li>
            <a href="#What-is-export-drayage">What is export drayage?</a>
          </li>
          <li>
            <a href="#What-Makes-a-Good-Drayage-Partner">
              What Makes a Good Drayage Partner?
            </a>
          </li>
          <li>
            <a href="#Drayage-Issues-in-2022">Drayage Issues in 2022</a>
          </li>
        </ul>
      </nav>

      <h2>Drayage: Understanding its Place in Supply Chain Logistics</h2>

      <p>
        As a shipper or final destination receiver, there’s a good chance you’re
        interested in only two or three data points. When will freight be picked
        up and when will it arrive? Those two data points will provide the
        third—how long will it take? For carriers, there’s a whole lot more
        involved.
      </p>
      <p>
        If you’re a shipper or consignee (the receiver), understanding the
        complexity of transporting freight should provide you with some
        invaluable insights into what moving cargo entails—and help you better
        prepare.
      </p>
      <p>
        It’s important to note that while the word dray referred to a cart or
        truck, modern drayage sometimes refers to the transport of cargo by sea
        and rail as well as by truck. In the case of truck and rail, they each
        make more sense for short hauls within a metropolitan area and are more
        cost-efficient.
      </p>
      <p>
        Drayage is a link in the supply chain, but it’s an important one. And at
        times, complicated. So what are the different facets of drayage, and
        more importantly, why is it such a vital link?
      </p>

      <h3 id="What-Are-the-Benefits-of-Port-Drayage">
        What Are the Benefits of Port Drayage?
      </h3>
      <p>
        According to The Office for Coastal Management, 76% of all trade in the
        US involved marine transportation. However, 90% of all international
        trade arrives via ocean ports.
      </p>
      <p>
        Since port terminals are simply a nexus in the global supply chain and
        intermodal logistics, transport to and from the port is necessary. This
        is where port drayage comes in.
      </p>
      <p>
        The math alone conveys the importance of drayage. Think of 90% of
        international trade not moving—in or out—of the port. Putting math aside
        and getting more specific, research from FreightWaves in 2019 reported
        more than 60 million drayage movements every year in North America. At
        the time, that represented a market of more than $50 billion.
      </p>

      <h4 id="What-is-import-drayage">What is import drayage?</h4>
      <p>
        Import drayage is the transport of a container—with a number or MBL
        number assigned to it—from an ocean terminal to a consignee’s warehouse.
        Containers are then unloaded and returned. This is how to get loads from
        the port.
      </p>

      <h4 id="What-is-export-drayage">What is export drayage?</h4>
      <p>
        Export drayage involves transporting cargo from the shipper’s warehouse.
        An empty container with a booking number is sent to the warehouse and
        loaded, then moved via truck or rail to the Port of Loading where it’s
        loaded on an ocean carrier.
      </p>
      <p>
        <strong>
          All of those loads coming and going is a complex process, so how is it
          managed?
        </strong>
      </p>
      <p>
        <strong>Load Matching</strong>
      </p>
      <p>
        Imagine a truck, train box, carrier ship, or anything else that didn’t
        take into account the goods it was transporting. Anything and everything
        was just piled on without any consideration to things like weight,
        dimensions, fragility, temperature needs, and expiry dates.
      </p>

      <p>
        It would be chaotic and costly. This is where load matching comes in.
        This process involves matching a vehicle with a suitable load and is the
        foundation for an efficient, cost-effective, and risk-free shipment.
      </p>

      <h5>Key Load Matching Metrics</h5>
      <p>
        There are several key factors when it comes to accurate—and
        profitable—load matching.
      </p>
      <h5>Load Capacity and Weight</h5>
      <p>
        Every vehicle has a load capacity. Overfilling it jeopardizes more than
        the cargo—it puts the vehicle and the driver at risk as well. And total
        weight isn’t the only factor. Everything needs to be packed with balance
        and weight distribution in mind as well.{' '}
      </p>
      <h5>Height</h5>
      <p>
        This has nothing to do with the height of individual pieces of cargo. It
        refers to how high cargo is packed onto the truck. If it’s stacked too
        high, you run the risk of your cargo getting stuck under a low bridge.
      </p>
      <h5>Fragility</h5>
      <p>
        Stacking heavy garden statues on top of crystal stemware is obviously a
        bad idea. But it may be an equally bad idea to even put them beside each
        other. Considering fragility is a must.
      </p>
      <h5>Reverse Order: First On, Last Off</h5>
      <p>
        Order delivery routes need to be factored in when loading a vehicle,
        otherwise, drivers and/or loading dock workers would be needlessly
        offloading and reloading time after time.
      </p>
      <p>
        Back in the day, the process of matching a load would have been done
        manually—and it probably took a lot of time and effort. Fortunately,
        here in the 21st century, technology can do the job.
      </p>
      <h4>Container Drayage Load Boards</h4>
      <h5>First up, what is a load board?</h5>
      <p>
        Container load boards, which some may call freight boards or freight
        matching services, are online marketplaces that connect shippers and
        carriers. Shippers, truckers, and freight brokers can post and find
        container loads that are available.
      </p>
      <p>
        Load boards for containers are typically sophisticated software that let
        you post or search for a load based on your set criteria. However, not
        all boards are created equal. The following is a list of typical
        features.
      </p>
      <ul>
        <li>
          Load matching. Loads are matched to carriers based on capacity and the
          other metrics mentioned above.
        </li>
        <li>
          Days-to-Pay. This is a calculation based on a company’s credit risk
          using the average time it takes to pay bills.
        </li>
        <li>Credit information. A company’s credit score or credit rating.</li>
        <li>
          Message board. A separate board within the board for more discussion.
        </li>
        <li>
          A field for notes. This could be somewhere to make notes or leave
          reviews.
        </li>
        <li>
          Federal Motor Carrier Safety Administration (FMCSA) verification.
          Requires a USDOT or docket number.
        </li>
        <li>An accompanying mobile app.</li>
      </ul>
      <p>
        If you’re looking to book container loads, you can choose a load board
        that will best suit your needs.
      </p>
      <h4>When Drayage Means Something Else</h4>
      <p>
        To avoid confusion, it’s worth noting that the term drayage is sometimes
        used in a different context. It could refer to fees and mean any of the
        following.{' '}
      </p>
      <ul>
        <li>Drayage rates</li>
        <li>Drayage charges</li>
        <li>Drayage costs</li>
      </ul>
      <p>
        And while drayage often means the short distance between a port and a
        final destination, it can also refer to transport to an intermodal point
        or terminal, or an inland/border point.{' '}
      </p>

      <h4 id="What-Makes-a-Good-Drayage-Partner">
        What Makes a Good Drayage Partner?
      </h4>
      <p>
        According to the Intermodal Association of North America (IANA), there
        is a list of classifications for drayage services. Since different goods
        have different transportation needs, there is no one size fits all
        method when it comes to drayage.
      </p>
      <p>Available options are:</p>
      <ul>
        <li>
          <strong>Door to door.</strong> These are goods being shipped directly
          to the consignee.
        </li>
        <li>
          <strong>Expedited drayage.</strong> Used for dated products such as
          food or any other time-sensitive goods that need to be transported
          quickly.
        </li>
        <li>
          <strong>Inter-carrier drayage.</strong> These containers will be moved
          from one carrier to another, for example, the handoff of goods from
          truck to rail or vice versa.
        </li>
        <li>
          <strong>Intra-carrier drayage.</strong> Similar to the above
          inter-carrier drayage except that this involves freight moved between
          two different hubs that are owned by the same carrier.
        </li>
        <li>
          <strong>Pier drayage.</strong> This refers to transport driven from a
          hub or terminal to a pier or harbor.
        </li>
        <li>
          <strong>Shuttle drayage.</strong> You would choose this type of
          drayage when you need to move cargo from an intermodal carrier to a
          warehouse or other temporary destination point.
        </li>
      </ul>
      <h3 id="Drayage-Issues-in-2022">Drayage Issues in 2022</h3>
      <p>
        The pandemic has wreaked havoc on the supply chain, and the drayage or
        first-mile segment of the industry is facing challenges as well. Current
        issues include:
      </p>
      <p>
        A rise in drayage rates. Estimates from June put rates at about 28%
        higher than last year, mostly due to port disruptions, delays,
        congestion, as well as equipment shortages.
      </p>
      <p>
        Rising truck insurance costs. Over the last 10 years, insurance premiums
        have increased by about 47% per mile.
      </p>
      <p>
        Driver shortages. The industry has lost tens of thousands of drivers
        over the last several years. Even before COVID, older drivers were
        retiring, but they weren’t and aren’t getting replaced by younger
        drivers.
      </p>
      <h2>Trust Loadsmart With Your Port Drayage Needs</h2>
      <p>
        Loadsmart’s platform connects you with data from port operators, ELD
        systems, TMSs, 3PLs, and WMSs. This means you have access to real-time
        visibility into when a container is discharged, available, or being
        held, and when a truck gates out. Information that ensures your
        containers are always returned on time, avoiding unnecessary extra fees
        and delays.
      </p>
    </>
  ),
};

const sidebarLinks = [
  {
    label: 'Loadsmart Smart(er) Drayage',
    url: 'https://loadsmart.com/port-drayage/',
    external: false,
  },
  {
    label: 'About AB-5: Capacity, Rates, and Next Steps for Carriers',
    url: 'https://blog.loadsmart.com/2021/06/03/about-ab-5-capacity-rates-and-next-steps-for-carriers/',
    external: true,
  },
  {
    label:
      'Learn With Loadsmart: What I Learned at TPM 2022, Where Building Relationships Mattered Most',
    url: 'https://blog.loadsmart.com/2022/04/13/learn-with-loadsmart-what-i-learned-at-tpm-2022-where-building-relationships-mattered-most/',
    external: true,
  },
];

const bannerContent = {
  logo: <LoadsmartLogo />,
  title: 'Smart(er) Drayage from Loadsmart',
  bullets: [
    'Instant pricing',
    'Trusted Carrier Partners',
    'Visibility and Tracking',
    'Reduce Excess Container Charges',
    'Real-time reporting',
  ],
  cta: 'Learn More',
  url: 'https://loadsmart.com/port-drayage/',
};

export default function WhatIsPD() {
  return (
    <>
      <Head>
        <meta
          type="keywords"
          content="port drayage, drayage, drayage meaning, drayage definition, drayage, what is drayage, drayage directory, drayage truck, railyards, drayage service, container drayage, container trucking, drayage companies, Drayage truck board, drayage trucks, container trucking company, define drayage, drayage carriers, drayage services, drayage trucking, intermodal drayage, air truck, dray meaning, drayage load board, intermodal companies, intermodal trucking companies, container load board, container trucking companies"
        />
      </Head>
      <NextSeo
        nofollow={process.env.NOINDEX_FLAG}
        noindex={process.env.NOINDEX_FLAG}
        title="What is Port Drayage | Drayage Services"
        description="Port drayage is the process of freight hauls from ports and other areas to nearby locations or the shipping of goods over a short distance."
        canonical="https://loadsmart.com/content/what-is-port-drayage/"
        openGraph={{
          url: 'https://loadsmart.com/content/what-is-port-drayage/',
        }}
      />
      <Content
        hero={heroImages}
        content={contentCopy}
        sidebar={sidebarLinks}
        banner={bannerContent}
      />
    </>
  );
}
